// Slides on a notes page (owner 2026-10-31): every NCERT chapter can carry the Part A
// (pre-filled) slides of its masterclass, made in the lecture-studio repo. The pictures and
// a small list live as plain files under `public/v2-slides/<CODE>/` (like the NCERT figures
// under `v2-figures/`), so adding a chapter's slides needs no backend change:
//
//   public/v2-slides/XII-05/slides.json
//   {
//     "chapter": "XII-05", "title": "Molecular Basis of Inheritance", "made": "2026-11-01",
//     "lecture": "2026-11-01-xii-05-molecular-basis-masterclass-16x9.html",
//     "slides": [
//       { "id": "dna-structure", "unit": "XII-05.1.1", "title": "Polynucleotide chain",
//         "image": "dna-structure.jpg" },
//       ...
//     ]
//   }
//
// `unit` is the code of the notes page the slide belongs to (the units of structure.json);
// a slide with no unit (the cover, say) is not shown on any page. Order in the list = order
// on the page. Images are 1600 × 900 JPEGs.
//
// On a page: the "Theory | Slides" switch (ViewSwitch) sits in the title band and appears only
// when the chapter has a slides file; "Slides" is greyed out on a page that has no slide.
// SlideViewer shows one slide at a time: Previous / Next, ← → keys (they move the slides
// first, then the page). The last choice is remembered on this laptop.
//
// Full screen (owner 2026-11-02: "continuous, distraction-free slides, as in a presentation"):
// the ⛶ button, a click on the slide or the F key opens SlidePresenter — the whole screen,
// black around the slide, nothing else. It runs through ALL the chapter's slides in page order
// (it does not stop at the end of a notes page). Controls fade away when the mouse is still.
// "Exit full screen" (or Esc) closes it, and the notes page opens on the page of the slide
// he was on, at that slide.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { C, FONT } from './theme';

const VIEW_KEY = 'v2_notes_view';            // 'theory' | 'slides'
export const SLIDES_ROOT = '/v2-slides';

export function chapterCode(chapter) {
  if (!chapter || !chapter.class_level || !chapter.ncert_no) return null;
  return `${chapter.class_level}-${String(chapter.ncert_no).padStart(2, '0')}`;
}

export function readView() {
  try { return localStorage.getItem(VIEW_KEY) === 'slides' ? 'slides' : 'theory'; } catch { return 'theory'; }
}
export function saveView(v) {
  try { localStorage.setItem(VIEW_KEY, v); } catch { /* private mode */ }
}

// The chapter's slides file, or null when the chapter has none (404 → null, never an error).
export function useChapterSlides(code) {
  return useQuery({
    queryKey: ['v2-slides', code],
    enabled: Boolean(code),
    staleTime: 10 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const r = await fetch(`${SLIDES_ROOT}/${code}/slides.json`, { cache: 'no-cache' });
      if (!r.ok) return null;
      const ct = r.headers.get('content-type') || '';
      if (!ct.includes('json')) return null;        // the SPA fallback page, not a file
      const data = await r.json();
      if (!data || !Array.isArray(data.slides)) return null;
      const slides = data.slides
        .filter((s) => s && s.image && s.id)
        .map((s) => ({ ...s, url: `${SLIDES_ROOT}/${code}/${s.image}` }));
      const byUnit = {};
      slides.forEach((s) => { if (s.unit) (byUnit[s.unit] = byUnit[s.unit] || []).push(s); });
      return { ...data, slides, byUnit };
    },
  });
}

const IconSlides = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="12" rx="2" /><path d="M12 16v4M8 20h8" />
  </svg>
);
const IconText = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 6h16M4 12h16M4 18h10" />
  </svg>
);
const IconArrowL = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
);
const IconArrowR = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
);

const IconFull = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
  </svg>
);
const IconExitFull = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
  </svg>
);

// True full screen must be asked for inside the click / key press itself, so the viewer calls
// this before it opens the presenter. In the desktop app the window fills the screen (the app
// allows the "fullscreen" permission, as for videos). If the browser refuses, the presenter
// still covers the whole window.
export function enterFullScreen() {
  try {
    const el = document.documentElement;
    if (!document.fullscreenElement && el.requestFullscreen) el.requestFullscreen().catch(() => {});
  } catch { /* not supported */ }
}
function leaveFullScreen() {
  try { if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {}); } catch { /* ignore */ }
}

// While the presenter is open the page's slide viewer ignores the keys (both listen on window).
let presenterOpen = false;

// "Theory | Slides" — two icon buttons in the slim page header (owner 2026-10-31: icons only;
// the tooltip carries the words; the slide count sits as a small badge).
export function ViewSwitch({ view, onChange, count }) {
  const seg = (key, title, icon, disabled, badge) => {
    const on = view === key;
    return (
      <button type="button" key={key} disabled={disabled} onClick={() => !disabled && onChange(key)}
        data-testid={`v2-view-${key}`} aria-pressed={on} aria-label={title} title={title} style={{
          position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 36, borderRadius: 9, border: 0,
          background: on ? C.primary : 'transparent', color: on ? '#FFFFFF' : disabled ? '#CBD5E1' : C.muted,
          cursor: disabled ? 'default' : 'pointer', padding: 0,
        }}>
        {icon}
        {badge != null && (
          <span style={{
            position: 'absolute', top: -6, right: -6, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
            background: on ? C.primaryDark : '#E2E8F0', color: on ? '#FFFFFF' : C.muted,
            fontFamily: FONT.ui, fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center',
          }}>{badge}</span>
        )}
      </button>
    );
  };
  return (
    <div role="group" aria-label="Theory or slides" data-testid="v2-view-switch" style={{
      display: 'inline-flex', alignItems: 'center', gap: 2, padding: 2, borderRadius: 11,
      background: '#F1F5F9', border: `1px solid ${C.border}`,
    }}>
      {seg('theory', 'Theory — the full notes', <IconText />, false)}
      {seg('slides', count ? `Slides — ${count} slide${count === 1 ? '' : 's'} for this page` : 'No slides for this page', <IconSlides />, !count, count || null)}
    </div>
  );
}

// One slide at a time. On the last slide → (and on the first ←) is left to the page's own
// handler, so the page turns. `startId` = open at this slide (after full screen closes);
// `onPresent(slideId)` opens the full-screen presenter at that slide.
export function SlideViewer({ slides, startId = null, onPresent }) {
  const n = slides.length;
  const [i, setI] = useState(0);
  const [loaded, setLoaded] = useState({});
  useEffect(() => {
    const k = startId ? slides.findIndex((s) => s.id === startId) : -1;
    setI(k >= 0 ? k : 0);
  }, [slides, startId]);
  const cur = slides[Math.min(i, n - 1)];
  const go = useCallback((d) => setI((k) => Math.max(0, Math.min(n - 1, k + d))), [n]);
  const present = useCallback(() => {
    if (!onPresent || !cur) return;
    enterFullScreen();
    onPresent(cur.id);
  }, [onPresent, cur]);

  // ← → move the slides first; at the ends the page turns. Capture phase so this runs
  // before the page's own arrow-key handler, which is then stopped.
  useEffect(() => {
    const onKey = (e) => {
      if (presenterOpen) return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if ((e.key === 'f' || e.key === 'F') && onPresent) { e.preventDefault(); e.stopPropagation(); present(); return; }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const d = e.key === 'ArrowLeft' ? -1 : 1;
      const edge = (d < 0 && i === 0) || (d > 0 && i === n - 1);
      if (edge) return;   // let the page turn
      e.preventDefault();
      e.stopPropagation();
      go(d);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [i, n, go, onPresent, present]);

  // the next picture loads quietly in the background
  useEffect(() => {
    const nx = slides[i + 1];
    if (nx && !loaded[nx.url]) { const im = new Image(); im.src = nx.url; }
  }, [i, slides, loaded]);

  const btn = (dir) => {
    const atEdge = dir < 0 ? i === 0 : i === n - 1;
    const label = dir < 0 ? 'Previous slide' : 'Next slide';
    return (
      <button type="button" onClick={() => go(dir)} disabled={atEdge} aria-label={label}
        data-testid={dir < 0 ? 'v2-slide-prev' : 'v2-slide-next'} style={{
          display: 'flex', alignItems: 'center', gap: 6, height: 44, padding: '0 16px', borderRadius: 12,
          border: `1px solid ${C.border}`, background: C.surface, color: atEdge ? '#CBD5E1' : C.text,
          fontFamily: FONT.ui, fontSize: 14, fontWeight: 600, cursor: atEdge ? 'default' : 'pointer',
        }}>
        {dir < 0 && <IconArrowL />}{dir < 0 ? 'Previous' : 'Next'}{dir > 0 && <IconArrowR />}
      </button>
    );
  };

  if (!cur) return null;
  return (
    <div data-testid="v2-slide-viewer" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        position: 'relative', borderRadius: 16, overflow: 'hidden', border: `1px solid ${C.border}`, background: '#FFFFFF',
        boxShadow: '0 10px 30px rgba(30,27,75,.10)',
      }}>
        <div onClick={present} title="Click for full screen" role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') present(); }}>
          <img src={cur.url} alt={cur.title || `Slide ${i + 1}`} draggable={false}
            onLoad={() => setLoaded((m) => (m[cur.url] ? m : { ...m, [cur.url]: true }))}
            style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'contain', background: '#FFFFFF', cursor: 'pointer' }} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {btn(-1)}
        <div style={{ flexGrow: 1, minWidth: 0, fontFamily: FONT.ui, fontSize: 15, color: C.text, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: C.primaryDark, whiteSpace: 'nowrap' }} data-testid="v2-slide-count">Slide {i + 1} of {n}</span>
          {cur.title && <span style={{ color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cur.title}</span>}
        </div>
        <span style={{ display: 'inline-flex', gap: 4 }} aria-hidden="true">
          {slides.slice(0, 24).map((s, k) => (
            <button type="button" key={s.id} onClick={() => setI(k)} title={s.title || `Slide ${k + 1}`} style={{
              width: 10, height: 10, borderRadius: 5, border: 0, padding: 0, cursor: 'pointer',
              background: k === i ? C.primary : '#CBD5E1',
            }} />
          ))}
        </span>
        {btn(1)}
        {onPresent && (
          <button type="button" onClick={present} data-testid="v2-slide-fullscreen"
            aria-label="Full screen — all the chapter's slides, like a presentation (F key)"
            title="Full screen — all the chapter's slides, like a presentation (F key)" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 12,
              border: `1px solid ${C.border}`, background: C.surface, color: C.text, cursor: 'pointer', padding: 0,
            }}>
            <IconFull />
          </button>
        )}
      </div>
      <div style={{ fontFamily: FONT.ui, fontSize: 13.5, color: C.muted }}>
        Click the slide (or press F) for full screen — the slides then run through the whole chapter.
        Use the ← → keys to move; on the last slide → turns the page.
        Switch to <b>Theory</b> for the full notes, word meanings and highlights.
      </div>
    </div>
  );
}

// Full-screen presentation of the whole chapter's slides (owner 2026-11-02).
// `slides` = every slide of the chapter in page order; `startId` = where to begin;
// `labelOf(unitCode)` = the page's name for the small caption; `onClose(slide)` = the slide
// he was on when he left (the notes page then opens there).
const IDLE_MS = 2500;

export function SlidePresenter({ slides, startId, labelOf, onClose }) {
  const n = slides.length;
  const [i, setI] = useState(() => Math.max(0, slides.findIndex((s) => s.id === startId)));
  const [awake, setAwake] = useState(true);       // controls showing
  const boxRef = useRef(null);
  const timer = useRef(null);
  const entered = useRef(false);                  // true full screen actually happened
  const iRef = useRef(i);
  iRef.current = i;
  const atEnd = i >= n;                           // the "end of slides" card after the last slide

  const close = useCallback(() => {
    leaveFullScreen();
    onClose(slides[Math.min(iRef.current, n - 1)]);
  }, [onClose, slides, n]);

  const go = useCallback((d) => setI((k) => Math.max(0, Math.min(n, k + d))), [n]);

  const wake = useCallback(() => {
    setAwake(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setAwake(false), IDLE_MS);
  }, []);

  useEffect(() => {
    presenterOpen = true;
    try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch { /* ignore */ }
    if (boxRef.current) boxRef.current.focus();
    wake();
    return () => { presenterOpen = false; clearTimeout(timer.current); leaveFullScreen(); };
  }, [wake]);

  // Esc (or the system's own exit) leaves full screen → the presenter closes too.
  useEffect(() => {
    const onChange = () => {
      if (document.fullscreenElement) entered.current = true;
      else if (entered.current) close();
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [close]);

  // Keys: → Space PageDown = next; ← PageUp Backspace = back; Home / End; Esc = exit.
  // Capture phase + stop, so the notes page underneath never reacts.
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const k = e.key;
      let used = true;
      if (k === 'ArrowRight' || k === ' ' || k === 'PageDown' || k === 'Enter') go(1);
      else if (k === 'ArrowLeft' || k === 'PageUp' || k === 'Backspace') go(-1);
      else if (k === 'Home') setI(0);
      else if (k === 'End') setI(n - 1);
      else if (k === 'Escape' || k === 'f' || k === 'F') close();
      else used = false;
      if (used) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [go, close, n]);

  // the next two pictures load quietly in the background
  useEffect(() => {
    [slides[i + 1], slides[i + 2]].forEach((s) => { if (s) { const im = new Image(); im.src = s.url; } });
  }, [i, slides]);

  const cur = slides[Math.min(i, n - 1)];
  if (!cur) return null;
  const pageName = cur.unit && labelOf ? labelOf(cur.unit) : '';

  // click on the left third = back, anywhere else = next (as in a presentation)
  const onStageClick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    go(e.clientX - r.left < r.width / 3 ? -1 : 1);
    wake();
  };

  const show = awake || atEnd;
  const fade = { opacity: show ? 1 : 0, transition: 'opacity .35s ease', pointerEvents: show ? 'auto' : 'none' };
  const roundBtn = (label, icon, onClick, disabled, testid) => (
    <button type="button" aria-label={label} title={label} disabled={disabled} data-testid={testid}
      onClick={(e) => { e.stopPropagation(); onClick(); wake(); }}
      style={{ ...bigBtn, opacity: disabled ? .35 : 1, cursor: disabled ? 'default' : 'pointer' }}>{icon}</button>
  );

  return (
    <div ref={boxRef} tabIndex={-1} data-testid="v2-slide-presenter" role="dialog" aria-label="Slides — full screen"
      onMouseMove={wake} style={{
        position: 'fixed', inset: 0, zIndex: 2000, background: '#000000', outline: 'none',
        cursor: show ? 'default' : 'none', userSelect: 'none',
      }}>
      {atEnd ? (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, color: '#FFFFFF', fontFamily: FONT.ui }}>
          <div style={{ fontSize: 26, fontWeight: 700 }}>End of the chapter's slides</div>
          <div style={{ fontSize: 16, opacity: .75 }}>{n} slides · press ← to go back</div>
        </div>
      ) : (
        <div onClick={onStageClick} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={cur.url} alt={cur.title || `Slide ${i + 1}`} draggable={false}
            style={{ maxWidth: '100vw', maxHeight: '100vh', width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
        </div>
      )}

      {/* top right: the way out — always the same place */}
      <div style={{ position: 'absolute', top: 16, right: 18, ...fade }}>
        <button type="button" onClick={(e) => { e.stopPropagation(); close(); }} data-testid="v2-slide-exit-fullscreen"
          aria-label="Exit full screen (Esc)" title="Exit full screen (Esc)" style={{
            display: 'flex', alignItems: 'center', gap: 8, height: 44, padding: '0 16px', borderRadius: 22,
            border: '1px solid rgba(255,255,255,.4)', background: 'rgba(15,23,42,.72)', color: '#FFFFFF',
            fontFamily: FONT.ui, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>
          <IconExitFull />Exit full screen
        </button>
      </div>

      {/* bottom: back / where am I / next */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 18, display: 'flex', justifyContent: 'center', ...fade }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '6px 8px', borderRadius: 28,
          background: 'rgba(15,23,42,.72)', color: '#FFFFFF', fontFamily: FONT.ui, fontSize: 14, maxWidth: '92vw',
        }}>
          {roundBtn('Previous slide (←)', <IconArrowL />, () => go(-1), i === 0, 'v2-present-prev')}
          <span data-testid="v2-present-count" style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
            {atEnd ? `${n} / ${n}` : `${i + 1} / ${n}`}
          </span>
          {!atEnd && pageName && (
            <span style={{ opacity: .75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '50vw' }}>{pageName}</span>
          )}
          {roundBtn('Next slide (→ or Space)', <IconArrowR />, () => go(1), atEnd, 'v2-present-next')}
        </div>
      </div>

      {/* a hair-thin progress line, fades with the controls */}
      <div style={{ position: 'absolute', left: 0, bottom: 0, height: 3, background: C.primary, width: `${(Math.min(i + 1, n) / n) * 100}%`, transition: 'width .25s ease', ...fade }} />
    </div>
  );
}

// Every slide of the chapter, in the order of its notes pages.
export function chapterSlides(deck, units) {
  if (!deck || !deck.byUnit || !units) return [];
  const out = [];
  units.forEach((u) => { (deck.byUnit[u.code] || []).forEach((s) => out.push(s)); });
  return out;
}

const bigBtn = {
  width: 44, height: 44, borderRadius: 22, border: '1px solid rgba(255,255,255,.4)', background: 'rgba(255,255,255,.12)',
  color: '#FFFFFF', fontFamily: FONT.ui, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// Which slides belong to a page.
export function slidesOf(deck, unitCode) {
  if (!deck || !deck.byUnit) return [];
  return deck.byUnit[unitCode] || [];
}
